import { createAsyncThunk } from "@reduxjs/toolkit";
import i18next from "i18next";
import { toast } from "react-toastify";
import { apiCall } from "services/api";

import { toFormData } from "utils/files";

import {
  PresenceFormData,
  WorkspaceFormData,
  WorkspacesFilters,
} from "./types";

export const getAllWorkspaces = createAsyncThunk(
  "/workspaces",
  async (filters: WorkspacesFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get("/workspaces", {
        params: filters,
      });

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUserWorkspaces = createAsyncThunk(
  "/workspaces/my",
  async (filters: WorkspacesFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get("/my/workspaces", {
        params: filters,
      });

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getWorkspace = createAsyncThunk(
  "/workspaces/:id",
  async (workspaceId: string, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get(`/workspaces/${workspaceId}`);

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const createWorkspace = createAsyncThunk(
  "/workspaces/create",
  async (workspace: WorkspaceFormData, { rejectWithValue }) => {
    try {
      const response = await apiCall().post("/workspaces", workspace);
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "createWorkspaceForm.workspaceCreated"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteWorkspace = createAsyncThunk(
  "/workspaces/delete",
  async (workspaceId: string, { rejectWithValue }) => {
    try {
      const response = await apiCall().delete(`/workspaces/${workspaceId}`);
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "createWorkspaceForm.workspaceDeleted"
        )
      );
      return { response: response.data, workspaceId: workspaceId };
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateWorkspace = createAsyncThunk(
  "/workspaces/update",
  async (
    {
      workspaceId,
      data,
    }: { workspaceId: string; data: Partial<WorkspaceFormData> },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().patch(
        `/workspaces/${workspaceId}`,
        data
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "createWorkspaceForm.workspaceUpdated"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateWorkspaceLogo = createAsyncThunk(
  "/workspaces/update/logo",
  async (
    { workspaceId, photo }: { workspaceId: string; photo: any },
    { rejectWithValue }
  ) => {
    const formData = toFormData({ photo: photo });
    try {
      const response = await apiCall().post(
        `/workspaces/${workspaceId}/logo`,
        formData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const presenceWorkspace = createAsyncThunk(
  "/workspaces/presence",
  async (
    {
      workspaceId,
      presence,
    }: { workspaceId: number; presence: PresenceFormData },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/workspaces/${workspaceId}/presence`,
        presence
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          presence.status === "accepted"
            ? "invitationAccepted"
            : "invitationDeclined"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const addUsersToWorkspace = createAsyncThunk(
  "/workspaces/addUsers",
  async (
    { workspaceId, users }: { workspaceId: number; users: number[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/workspaces/${workspaceId}/users`,
        { users: users, invite_url: `${process.env.REACT_APP_RETURN_URL}/` }
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "createWorkspaceForm.usersAdded"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteUserFromWorkspace = createAsyncThunk(
  "/workspaces/deleteUser",
  async (
    { workspaceId, userId }: { workspaceId: number; userId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `/workspaces/${workspaceId}/users/${userId}`
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "createWorkspaceForm.userDeleted"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const linkAnotherWorkspace = createAsyncThunk(
  "/workspaces/id/linked/id",
  async (
    {
      workspaceId,
      workspaceToBeLinkedId,
    }: { workspaceId: number; workspaceToBeLinkedId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/workspaces/${workspaceId}/linked/${workspaceToBeLinkedId}`
      );

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const linkAnotherWorkspaceStatus = createAsyncThunk(
  "/workspaces/id/linked/id/status",
  async (
    {
      workspaceId,
      workspaceToBeLinkedId,
      status,
    }: {
      workspaceId: number;
      workspaceToBeLinkedId: number;
      status: "linked" | "rejected";
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/workspaces/${workspaceId}/linked/${workspaceToBeLinkedId}/status`,
        {
          link_status: status,
        }
      );

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeWorkspaceLink = createAsyncThunk(
  "/workspaces/id/linked/id/delete",
  async (
    {
      workspaceId,
      workspaceToBeLinkedId,
    }: {
      workspaceId: number;
      workspaceToBeLinkedId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `/workspaces/${workspaceId}/linked/${workspaceToBeLinkedId}`
      );

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
